import BigNumber from 'bignumber.js'

export { BigNumber }

BigNumber.config({ DECIMAL_PLACES: 2, ROUNDING_MODE: BigNumber.ROUND_HALF_UP })

/** 格式化价格（分转元） */
export function formatPrice(price: number, fromFen = false): string {
  const val = fromFen ? new BigNumber(price).div(100) : new BigNumber(price)
  return val.toFormat(2)
}

/** 计算折扣价格 */
export function calcDiscountPrice(originalPrice: number, discountAmount: number): number {
  const original = new BigNumber(originalPrice || 0)
  return BigNumber.max(original.minus(discountAmount), 0).toNumber()
}

/** 计算折扣率（0-100） */
export function calcDiscountRate(originalPrice: number, currentPrice: number): number {
  if (!originalPrice || originalPrice <= 0) return 100
  return new BigNumber(currentPrice).div(originalPrice).times(100).toNumber()
}

/** 获取最低价 */
export function getLowestPrice(prices: number[]): number {
  if (!prices.length) return 0
  return Math.min(...prices)
}

/** 价格区间文本 */
export function getPriceRangeText(minPrice: number, maxPrice: number): string {
  if (minPrice === maxPrice) return formatPrice(minPrice)
  return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
}

/** 拼接价格单位文本 */
export function getPriceText(price: number, unit = '起'): string {
  return `¥${formatPrice(price)}${unit}`
}

/** 产品价格信息（用于列表页展示价格计算） */
export interface ProductPriceInfo {
  productId?: string | number
  originalPrice?: number
  lowestPrice?: number
  couponDiscountPrice?: number
  discountAmount?: number
  discountTotalAmount?: number
  discountType?: number
  discountValue?: number
}

/**
 * 计算产品展示价格：原价减去优惠金额（同步版本）
 * 列表页模式：优先使用 discountTotalAmount
 */
export function calculateDisplayLowestPrice(product?: ProductPriceInfo): number {
  if (!product?.originalPrice) {
    if (product?.couponDiscountPrice) return product.couponDiscountPrice
    if (product?.lowestPrice) return product.lowestPrice
    return 0
  }

  let discountAmount = new BigNumber(0)

  if (product?.discountAmount !== undefined && product?.discountAmount !== null) {
    discountAmount = new BigNumber(product.discountAmount || 0)
  } else if (product?.discountTotalAmount !== undefined && product?.discountTotalAmount !== null) {
    discountAmount = new BigNumber(product.discountTotalAmount || 0)
  } else if (product?.discountType && product?.discountValue) {
    if (product.discountType === 1) {
      discountAmount = new BigNumber(product.discountValue || 0)
    } else if (product.discountType === 2) {
      const originalPrice = new BigNumber(product.originalPrice || 0)
      const discountRate = new BigNumber(1).minus(product.discountValue || 0)
      discountAmount = originalPrice.multipliedBy(discountRate)
    }
  }

  const originalPrice = new BigNumber(product.originalPrice || 0)
  const calculatedPrice = originalPrice.minus(discountAmount)
  const result = calculatedPrice.isGreaterThan(0)
    ? calculatedPrice.toNumber()
    : product?.lowestPrice || 0

  return result
}

/**
 * 计算产品展示价格（异步版本，与同步逻辑一致）
 * @param product 产品对象
 * @param isDetailPage 是否为详情页模式
 */
export async function calculateDisplayLowestPriceAsync(
  product?: ProductPriceInfo,
  isDetailPage = false
): Promise<number> {
  if (!product?.originalPrice) {
    if (product?.couponDiscountPrice) return product.couponDiscountPrice
    if (product?.lowestPrice) return product.lowestPrice
    return 0
  }

  let discountAmount = new BigNumber(0)

  if (isDetailPage) {
    discountAmount = new BigNumber(product?.discountAmount || 0)
  } else if (product?.discountTotalAmount !== undefined && product?.discountTotalAmount !== null) {
    discountAmount = new BigNumber(product.discountTotalAmount || 0)
  } else if (product?.discountType && product?.discountValue) {
    if (product.discountType === 1) {
      discountAmount = new BigNumber(product.discountValue || 0)
    } else if (product.discountType === 2) {
      const originalPrice = new BigNumber(product.originalPrice || 0)
      const discountRate = new BigNumber(1).minus(product.discountValue || 0)
      discountAmount = originalPrice.multipliedBy(discountRate)
    }
  }

  const originalPrice = new BigNumber(product.originalPrice || 0)
  const calculatedPrice = originalPrice.minus(discountAmount)
  const result = calculatedPrice.isGreaterThan(0) ? calculatedPrice.toNumber() : 0

  return result
}
